import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';

interface Props {
  open: boolean;
  onClose: () => void;
}

function prettyJSON(s: string | null | undefined): string {
  if (!s) return '-';
  try {
    const parsed = JSON.parse(s);
    return JSON.stringify(parsed, null, 0);
  } catch {
    return s;
  }
}

function fmtAction(a: string): string {
  const map: Record<string, string> = {
    USAJILI_DAILY_CREATE: 'Create daily row',
    USAJILI_DAILY_UPDATE: 'Update daily row',
    USAJILI_DAILY_DELETE: 'Delete daily row',
    USAJILI_DAILY_BULK_UPLOAD: 'Bulk upload',
    USAJILI_LOCREF_CREATE: 'Create location ref',
    USAJILI_LOCREF_UPDATE: 'Update location ref',
    USAJILI_LOCREF_DELETE: 'Delete location ref',
    USAJILI_EXPORT: 'Export',
    USAJILI_LOGIN: 'Login',
    USAJILI_LOGOUT: 'Logout',
  };
  return map[a] || a;
}

export const USAJILIAuditLogDialog: React.FC<Props> = ({ open, onClose }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['usajili_auditlog', page, pageSize, open],
    queryFn: () => usajiliApi.auditLog({ page, page_size: pageSize }),
    enabled: open,
  });

  const total = data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const results = data?.results || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>USAJILI Audit log — who changed what, when</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Admin-only. Every row below is immutable and cannot be edited.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="aud-ps">Rows per page</InputLabel>
              <Select
                labelId="aud-ps"
                label="Rows per page"
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[25, 50, 100, 250].map((n) => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={() => void refetch()}>
              Refresh
            </Button>
            <span>
              Showing <b>{Math.min((page - 1) * pageSize + 1, total)}</b> to{' '}
              <b>{Math.min(page * pageSize, total)}</b> of{' '}
              <b>{total.toLocaleString()}</b>
            </span>
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as any)?.error?.message || 'Failed to load audit log'}
          </Alert>
        )}
        <Paper sx={{ width: '100%', overflow: 'auto', maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 170 }}>
                  Timestamp
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 140 }}>
                  Who
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 150 }}>
                  Action
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 160 }}>
                  Entity
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 240 }}>
                  Old values
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 240 }}>
                  New values
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default', minWidth: 140 }}>
                  IP
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                    No audit records yet.
                  </TableCell>
                </TableRow>
              )}
              {results.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {new Date(a.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {a.user_name}{' '}
                    <span style={{ color: '#888', fontSize: 12 }}>(uid={a.user_id})</span>
                  </TableCell>
                  <TableCell>
                    <b>{fmtAction(a.action)}</b>
                  </TableCell>
                  <TableCell>
                    {a.entity_type.replace('USAJILI_', '')}
                    {a.entity_id ? (
                      <span style={{ color: '#666', fontSize: 12 }}> #{a.entity_id}</span>
                    ) : null}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      color: '#666',
                    }}
                  >
                    {prettyJSON(a.old_values)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      color: '#166534',
                    }}
                  >
                    {prettyJSON(a.new_values)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
                    {a.ip_address}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            size="small"
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            First
          </Button>
          <Button
            size="small"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Prev
          </Button>
          <Button size="small" disabled>
            Page {page} / {totalPages}
          </Button>
          <Button
            size="small"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
